/**
 * @file: GaugeWidget.tsx
 *
 * Renders the inspection composite-score gauge a chat bot posts via a
 * ` ```gauge ` fence: the semicircle score gauge (综合评分) on top, and a row
 * of severity ring-counters below it. Width is taken from the bounded chat
 * host (`ChartWidthContext`) so it never forces the bubble full-width.
 *
 * Fence payload: `{ score, label?, buckets: [{ label, count, color }] }`.
 */

import {
  ScoreGaugeView,
  useDefaultScoreGaugeStyle,
} from '@euler/app/components/score/ScoreGauge';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Easing, Text, View } from 'react-native';
import { ChartWidthContext } from './ChartWidget';
import { GaugeBucket, GaugePayload } from './payloads';

/** The gauge's natural box aspect: it's a 270°-arc dial with a bottom opening,
 * so its bounding height is `(1 + √2/2)/2` of its width. Feeding the canvas
 * this exact ratio makes the dial fill the width (radius = width/2) instead of
 * being squeezed — which is what kept the score number oversized. */
const GAUGE_ASPECT = (1 + Math.SQRT2 / 2) / 2;
/** Cap the canvas in real px so a wide (desktop) bubble doesn't balloon it. */
const GAUGE_MAX_WIDTH = 300;
/** Horizontal breathing room (per side) between the dial and the card edge. */
const GAUGE_H_INSET = 20;

/** One severity row: label · proportional colored bar · count. */
const SeverityBar = memo(
  ({
    bucket,
    max,
    index,
  }: {
    bucket: GaugeBucket;
    max: number;
    index: number;
  }) => {
    const { rpx } = useResponsiveContext();
    const pct = max > 0 ? bucket.count / max : 0;
    // A non-zero count always shows at least a sliver so it's never invisible.
    const fillPct = bucket.count > 0 ? Math.max(pct * 100, 6) : 0;

    // The fill is rendered at its FULL width and wiped in via `translateX`
    // behind the clipping track — that reads as a grow-from-left but runs on
    // the native/UI thread (no per-frame layout, unlike animating `width`).
    // Needs the track's pixel width, so wait for onLayout before animating.
    const [trackWidth, setTrackWidth] = useState(0);
    const fillWidth = (trackWidth * fillPct) / 100;
    const progress = useRef(new Animated.Value(0)).current;
    useEffect(() => {
      if (trackWidth === 0) return;
      progress.setValue(0);
      const anim = Animated.timing(progress, {
        toValue: 1,
        duration: 500,
        delay: index * 90,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      });
      anim.start();
      return () => anim.stop();
    }, [progress, index, fillWidth, trackWidth]);
    const translateX = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [-fillWidth, 0],
    });
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: rpx(8),
        }}
      >
        <Text
          numberOfLines={1}
          style={{ width: rpx(64), fontSize: rpx(12), color: '#64748B' }}
        >
          {bucket.label}
        </Text>
        <View
          onLayout={e => setTrackWidth(e.nativeEvent.layout.width)}
          style={{
            flex: 1,
            height: rpx(8),
            backgroundColor: '#EEF1F5',
            borderRadius: rpx(4),
            overflow: 'hidden',
            marginHorizontal: rpx(8),
          }}
        >
          <Animated.View
            style={{
              width: fillWidth,
              height: '100%',
              backgroundColor: bucket.color,
              borderRadius: rpx(4),
              transform: [{ translateX }],
            }}
          />
        </View>
        <Text
          style={{
            width: rpx(22),
            fontSize: rpx(13),
            fontWeight: '700',
            color: bucket.color,
            textAlign: 'right',
          }}
        >
          {bucket.count}
        </Text>
      </View>
    );
  },
);

export const GaugeWidget = memo(({ data }: { data: GaugePayload }) => {
  const { rpx } = useResponsiveContext();
  const boundedWidth = useContext(ChartWidthContext);
  const rawScore = Number(data?.score);
  const score = Number.isFinite(rawScore)
    ? Math.max(0, Math.min(100, rawScore))
    : 0;
  const buckets: GaugeBucket[] = Array.isArray(data?.buckets)
    ? data.buckets
    : [];
  // Shared scale so bar lengths are comparable across buckets.
  const maxCount = Math.max(1, ...buckets.map(b => Number(b.count) || 0));

  // Inset the dial from the card edges so it doesn't span the full width.
  const available = (boundedWidth ?? GAUGE_MAX_WIDTH) - GAUGE_H_INSET * 2;
  const gaugeWidth = Math.min(Math.max(available, 0), GAUGE_MAX_WIDTH);
  const gaugeHeight = gaugeWidth * GAUGE_ASPECT;

  // The default score font is `rpx(68)` and the gauge then multiplies it by
  // `width/358` — on a wide surface that double-scales into a huge number.
  // A constant keeps the score a fixed fraction (~19%) of the gauge width.
  const baseTheme = useDefaultScoreGaugeStyle();
  const gaugeTheme = useMemo(
    () => ({ ...baseTheme, scoreFontSize: 68 }),
    [baseTheme],
  );

  return (
    <View
      style={{
        backgroundColor: '#F8FAFC',
        borderRadius: rpx(10),
        paddingVertical: rpx(12),
        marginVertical: rpx(6),
      }}
    >
      <View style={{ alignItems: 'center' }}>
        <ScoreGaugeView
          score={score}
          label="综合评分"
          width={gaugeWidth}
          height={gaugeHeight}
          theme={gaugeTheme}
        />
      </View>
      {buckets.length > 0 ? (
        <View style={{ marginTop: rpx(18), paddingHorizontal: rpx(14) }}>
          {buckets.map((b, i) => (
            <SeverityBar key={i} bucket={b} max={maxCount} index={i} />
          ))}
        </View>
      ) : null}
    </View>
  );
});
